/**
 * main.js — Entry point
 *
 * Boot sequence:
 *   1. Check for Spotify auth callback (?code= in URL) — handle before anything else
 *   2. Init SpotifyBridge — restore session if tokens exist in localStorage
 *   3. Wait for MusicKit JS to load, then init MusicKitBridge
 *   4. Init Webamp
 *   5. Init WebampController with the default bridge (Apple Music)
 *   6. Init both panels (ApplePanel + SpotifyPanel)
 *   7. Wire service selector to switchBridge()
 *   8. Init VisualizerBridge
 */

import Webamp from 'webamp';
import { MusicKitBridge } from './musickit-bridge.js';
import { SpotifyBridge }  from './spotify-bridge.js';
import { WebampController } from './webamp-controller.js';
import { ApplePanel }     from './apple-panel.js';
import { SpotifyPanel }   from './spotify-panel.js';
import { VisualizerBridge } from './visualizer-bridge.js';
import { DEVELOPER_TOKEN, SPOTIFY_CLIENT_ID } from './config.js';
import './styles.css';

// ─────────────────────────────────────────────────────────────
// Step 1 — Handle Spotify auth callback before anything else.
//           Spotify redirects back with ?code=xxx after sign-in.
// ─────────────────────────────────────────────────────────────

const spotifyBridge = new SpotifyBridge(SPOTIFY_CLIENT_ID);
let spotifyCallbackHandled = false;

(async () => {
  spotifyCallbackHandled = await spotifyBridge.handleCallbackIfPresent();
  if (spotifyCallbackHandled) {
    // Restore the session (tokens were just stored by handleCallbackIfPresent)
    spotifyBridge.restoreSession();
  } else {
    spotifyBridge.restoreSession();
  }
})();

// ─────────────────────────────────────────────────────────────
// Main bootstrap (waits for MusicKit)
// ─────────────────────────────────────────────────────────────

async function bootstrap() {
  // 1. Init MusicKit bridge
  const mkBridge = new MusicKitBridge(DEVELOPER_TOKEN);
  await mkBridge.init();

  // 2. Init Webamp
  const webamp = new Webamp({
    initialTracks: [
      {
        url: '/silent.mp3',
        metaData: {
          artist: 'Webamp × Music',
          title: '← Click "Music" to connect Apple Music or Spotify',
        },
        duration: 1,
      },
    ],
  });

  await webamp.renderWhenReady(document.getElementById('webamp-container'));

  // 3. Wire Webamp ↔ default bridge (Apple Music)
  const webampController = new WebampController(webamp, mkBridge);
  webampController.init();

  // 4. Init Apple Music panel
  const applePanel = new ApplePanel(mkBridge, webamp, webampController);
  applePanel.init();

  // 5. Init Spotify panel
  const spotifyPanel = new SpotifyPanel(spotifyBridge, webamp, webampController);
  spotifyPanel.init();

  // If Spotify callback was just handled, switch to Spotify view immediately
  // and init the player (it already has a valid token)
  if (spotifyCallbackHandled && spotifyBridge.isAuthorized) {
    _switchService('spotify', mkBridge, spotifyBridge, webampController, vizBridge);
    try {
      await spotifyBridge.initPlayer();
      spotifyPanel._showMainScreen();
      spotifyPanel._loadPlaylists();
    } catch (err) {
      console.error('[main] Spotify player init failed:', err);
    }
  }

  // 6. Wire the service selector buttons
  document.querySelectorAll('.amp-service-btn').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const service = btn.dataset.service;
      _switchService(service, mkBridge, spotifyBridge, webampController, vizBridge);

      // First time selecting Spotify — init the player
      if (service === 'spotify' && !spotifyBridge._player && spotifyBridge.isAuthorized) {
        try {
          await spotifyBridge.initPlayer();
          spotifyPanel._showMainScreen();
          spotifyPanel._loadPlaylists();
        } catch (err) {
          console.error('[main] Spotify player init failed:', err);
          alert(err.message); // surface Premium errors
        }
      }
    });
  });

  // 7. Init visualizer bridge — starts synthetic immediately
  const vizBridge = new VisualizerBridge(webamp, mkBridge);
  await vizBridge.init();

  const vizBtn = document.getElementById('amp-viz-btn');
  if (vizBtn) {
    const updateVizBtn = (state) => {
      if (state === 'live') {
        vizBtn.textContent = '🎛 Live Viz ✓';
        vizBtn.classList.add('amp-viz-btn--live');
        vizBtn.title = 'Visualizer is using real tab audio.';
      } else if (state === 'synthetic') {
        vizBtn.textContent = '🎛 Enable Live Viz';
        vizBtn.classList.remove('amp-viz-btn--live');
        vizBtn.title = 'Click to capture real audio for the visualizer';
      } else {
        vizBtn.textContent = '🎛 Viz unavailable';
        vizBtn.disabled = true;
      }
    };

    vizBridge.onStateChange = updateVizBtn;
    updateVizBtn(vizBridge.state);

    vizBtn.addEventListener('click', async () => {
      vizBtn.textContent = '🎛 Requesting…';
      vizBtn.disabled = true;
      const ok = await vizBridge.requestTabCapture();
      vizBtn.disabled = false;
      if (!ok) updateVizBtn(vizBridge.state);
    });
  }

  // 8. Expose globals for debugging
  window.__wam = { webamp, mkBridge, spotifyBridge, webampController, applePanel, spotifyPanel, vizBridge };

  console.log(
    '%c🎵 Webamp × Music ready!',
    'font-size:14px;color:#fa243c;font-weight:bold;'
  );
}

// ─────────────────────────────────────────────────────────────
// Service switching helper
// ─────────────────────────────────────────────────────────────

function _switchService(service, mkBridge, spotifyBridge, webampController, vizBridge) {
  const isApple   = service === 'apple';
  const isSpotify = service === 'spotify';

  // Update selector button styles
  document.querySelectorAll('.amp-service-btn').forEach((b) => {
    b.classList.toggle('amp-service-btn--active', b.dataset.service === service);
  });

  // Show / hide content areas
  document.getElementById('amp-apple-content').classList.toggle('amp-service-content--hidden', !isApple);
  document.getElementById('amp-spotify-content').classList.toggle('amp-service-content--hidden', !isSpotify);

  // Update panel title
  document.getElementById('amp-panel-title').textContent =
    isApple ? 'Apple Music' : 'Spotify';

  // Switch the active bridge in WebampController + VisualizerBridge
  if (isApple && webampController.mk !== mkBridge) {
    webampController.switchBridge(mkBridge);
    vizBridge?.setBridge(mkBridge);
    vizBridge?.deactivateExternalAnalyser(); // revert to synthetic for Apple Music
  } else if (isSpotify && webampController.mk !== spotifyBridge) {
    webampController.switchBridge(spotifyBridge);
    vizBridge?.setBridge(spotifyBridge);
    // No action needed — interceptor re-activates automatically when Spotify plays
  }
}

// ─────────────────────────────────────────────────────────────
// Entry
// ─────────────────────────────────────────────────────────────

if (typeof MusicKit !== 'undefined') {
  bootstrap().catch(console.error);
} else {
  document.addEventListener('musickitloaded', () => {
    bootstrap().catch(console.error);
  });

  setTimeout(() => {
    if (typeof MusicKit === 'undefined') {
      console.warn('[Webamp × Music] MusicKit JS has not loaded yet.');
    }
  }, 8000);
}
